import type { AxiosInstance } from 'axios';
import { AxiosError } from 'axios';
import type { Outcome } from '../../types/Outcome';
import type { Domain, FeatureTemplate, FeatureTemplateCategory, OutcomeGroup } from '../../types/Domains';

export const DomainService = (api: AxiosInstance) => ({

  // Get all verticals
  getVerticals: async () => {
    try {
      const response = await api.get<Map<string, string>>(`/verticals`);
      return response.data;
    } catch (error) {
      console.error('Error getting verticals:', error);
      return [];
    }
  },

  // Get domains for a given vertical
  getDomains: async (verticalId: string, includeOutcomes: boolean = false) => {
    try {
      //add include=outcomes if includeOutcomes is true
      const includeParam = includeOutcomes ? '?include=outcomes' : '';
      const response = await api.get<Domain[]>(`/verticals/${verticalId}/domains${includeParam}`);
      console.log("getDomains: ",response);
      // sort by displayOrder
      response.data.sort((a, b) => a.displayOrder - b.displayOrder);
      //filter out archived domains
      // response.data = response.data.filter(domain => domain.status !== 'archived');
      // console.log("getDomains after sort and filter: ",response);
      return response.data;
    } catch (error) {
      console.error('Error getting domains:', error);
      return [];
    }
  },

  // get outcomes for a given domain
  getOutcomes: async (domainId: string) => {
    try {
      console.log('getting outcomes at url:', api.defaults.baseURL + `/domains/${domainId}/outcomes`);
      const response = await api.get<Outcome[]>(`/domains/${domainId}/outcomes`);
      console.log('outcomes:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting outcomes:', error);
      return [];
    }
  },

  createOutcome: async (domainId: string, outcome: Outcome) => {
    try {
      const response = await api.post<Outcome>(`/domains/${domainId}/outcomes`, outcome);
      return response.data;
    } catch (error) {
      console.error('Error creating outcome:', error);
      return null;
    }
  },

  updateOutcome: async (domainId: string, outcomeId: string, outcome: Outcome) => {
    try {
      console.log('updating outcome at url:', api.defaults.baseURL + `/domains/${domainId}/outcomes/${outcomeId}`);
      await api.put<Outcome>(`/domains/${domainId}/outcomes/${outcomeId}`, outcome);
      return outcome;
    } catch (error) {
      console.error('Error updating outcome:', error);
      return null;
    }
  },

  // Outcome Groups
  getOutcomeGroups: async (domainId: string, status?: 'active' | 'archived') => {
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;

      const response = await api.get<OutcomeGroup[]>(`/domains/${domainId}/outcome-groups`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting outcome groups:', error);
      return [];
    }
  },

  getOutcomeGroup: async (domainId: string, groupId: string) => {
    try {
      const response = await api.get<OutcomeGroup>(`/domains/${domainId}/outcome-groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting outcome group:', error);
      return null;
    }
  },

  createOutcomeGroup: async (domainId: string, group: Partial<OutcomeGroup>) => {
    try {
      const response = await api.post<OutcomeGroup>(`/domains/${domainId}/outcome-groups`, group);
      return response.data;
    } catch (error) {
      console.error('Error creating outcome group:', error);
      return null;
    }
  },

  updateOutcomeGroup: async (domainId: string, groupId: string, group: Partial<OutcomeGroup>) => {
    try {
      const response = await api.put<OutcomeGroup>(`/domains/${domainId}/outcome-groups/${groupId}`, group);
      return response.data;
    } catch (error) {
      console.error('Error updating outcome group:', error);
      return null;
    }
  },

  // get feature templates for a given domain
  getFeatureTemplates: async (domainId: string, outcome?: string, category?: string, search?: string, pageSize?: number, pageNumber?: number, sort?: string) => {
    try {
      const params: Record<string, string> = {};

      // Only add params that have values
      if (outcome) params.outcome = outcome;
      if (category) params.category = category;
      if (search) params.search = search;
      
      // Add pagination params if provided
      if (pageSize !== undefined) {
        params.pageSize = pageSize.toString();
      }
      if (pageNumber !== undefined) {
        params.pageNumber = pageNumber.toString();
      }
      
      // Add sort param if provided
      if (sort) {
        params.sort = sort;
      }
      
      console.log('getFeatureTemplates - params being sent:', params);
      console.log('getFeatureTemplates - URL:', `/domains/${domainId}/feature-templates`);
      
      const response = await api.get<{
        items: FeatureTemplate[];
        pagination: {
          totalItems: number;
          totalPages: number;
          pageNumber: number;
          pageSize: number;
        };
      }>(`/domains/${domainId}/feature-templates`, { params });
      
      return response.data;
    } catch (error) {
      console.error('Error getting features:', error);
      if (error instanceof AxiosError && error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      return {
        items: [],
        pagination: {
          totalItems: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize: 10
        }
      };
    }
  },

  // get feature template categories for a given domain
  //TODO: we might have different categories for different outcomes, so we should probably pass in the outcome as well.
  //      For now, we'll just use all categories, and we could just filter locally if type becomes an issue.
  getFeatureTemplateCategories: async (domainId: string) => {
    try {
      const response = await api.get<FeatureTemplateCategory[]>(`/domains/${domainId}/categories?type=feature`);
      return response.data;
    } catch (error) {
      console.error('Error getting feature template categories:', error);
      return [];
    }
  },

  // create a feature template
  createFeatureTemplate: async (featureTemplate: FeatureTemplate) => {
    try {
      console.log("Creating feature template: ", featureTemplate);
      const response = await api.post<FeatureTemplate>(`/domains/${featureTemplate.domainId}/feature-templates`, featureTemplate);
      console.log('feature template created:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating feature template:', error);
      return null;
    }
  },

  // update a feature template
  updateFeatureTemplate: async (featureTemplate: FeatureTemplate) => {
    try {
      console.log("Updating feature template: ", featureTemplate);
      const response = await api.put<FeatureTemplate>(`/domains/${featureTemplate.domainId}/feature-templates/${featureTemplate.id}`, featureTemplate);
      return response.data;
    } catch (error) {
      console.error('Error updating feature template:', error);
      return null;
    }
  },

  // delete a feature template
  deleteFeatureTemplate: async (domainId: string, featureTemplateId: string) => {
    try {
      const response = await api.delete<FeatureTemplate>(`/domains/${domainId}/feature-templates/${featureTemplateId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting feature template:', error);
      return null;
    }
  }
  

}); 